/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import clsx from 'clsx';
import React, { useCallback } from 'react';
import { FastField, Field } from 'formik';
import { useEventCallback } from '@mui/material';
import { IField, Form } from '../interfaces/form';
import { isFireboltOptionalKeyDisabled } from '../utils/schemaBuilder';
import styles from './styles.module.scss';
import { SourceSchema } from './source-schema/SourceSchema';
import { HdCheckbox, HdIcon, HdIconButton, HdRadio, HdRadioGroup, HdTooltip } from '../../../components';
import {
  HdFormikDropDown,
  HdFormikTextField,
  shouldUpdateFormikDropdownFastField,
  shouldUpdateFormikTextFastField
} from '../../../form/formik/components';
import HdFormikCheckBox from '../../../form/formik/components/checkbox/HdFormikCheckBox';
import { FireboltTableTypeEnum } from '../interfaces/interface';
import { BigQueryClusterKeyTooltipMessage } from './ToolTip';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';

const fieldClassName = {
  'MuiOutlinedInput-root': {
    height: '40px',
    padding: '0 !important'
  },
  'MuiOutlinedInput-input': {
    height: '40px',
    padding: '0 var(--spacer)!important',
    borderRadius: 'var(--spacer) * 0.25'
  }
};

interface SchemaBuilderRowProps {
  field: Form;
  idx: number;
  bigQueryClusterKeysLimitReached: boolean;
  selectedPartitionKey: string;
  selectedDistKey: string;
  fieldsLength: number;

  dataTypes: any[];
  dataTypesLoading: boolean;
  fireboltTableType: any;
  options: {
    isFireboltDest: boolean;
    isBQDest: boolean;
    isDataBricks: boolean;
    isSnowFlake: boolean;
    isRedshift: boolean;
    withMappingData: boolean;
    isMySql: boolean;
  };
  highlightIds: React.MutableRefObject<Map<String, any>>;

  moveRowUp: (idx: number) => void;
  moveRowDown: (idx: number) => void;
  forceUpdate: () => void;
  handleDataTypeChangeEvent: (event: any, field: any) => void;
  isPrimaryKeyDisabled: (dataType: any) => boolean;
  isBigQueryClusterKeyDisabled: (field: any) => boolean;
  toggleKey: (idx: number, key: string) => void;
  handleRadioKeyChange: (field: IField, path: string) => void;
  isPartitionKeyDisabled: (dataType: any) => boolean;
  beforeRowDelete: (field: IField) => void;
}

export function SchemaBuilderRow({
  field,
  idx,
  moveRowUp,
  moveRowDown,
  highlightIds,
  toggleKey,
  forceUpdate,
  handleDataTypeChangeEvent,
  isPrimaryKeyDisabled,
  beforeRowDelete,
  bigQueryClusterKeysLimitReached,
  isPartitionKeyDisabled,
  fireboltTableType,
  isBigQueryClusterKeyDisabled,
  selectedPartitionKey,
  selectedDistKey,
  options,
  handleRadioKeyChange,
  dataTypes,
  dataTypesLoading,
  fieldsLength
}: SchemaBuilderRowProps) {
  const dataIdGenerator = useCallback(getDataIdGenerator(`row-${idx}`), [idx]);

  const {
    isFireboltDest,
    isBQDest,
    isDataBricks,
    isSnowFlake,
    isRedshift,
    withMappingData,
    isMySql
  } = options;

  const handlePartitionKeyChange = useEventCallback(() => {
    handleRadioKeyChange(field, 'partitionKey');
  });

  const handleDistKeyChange = useEventCallback(() => {
    handleRadioKeyChange(field, 'distKey');
  });

  const moveUp = useEventCallback(() => moveRowUp(idx));
  const moveDown = useEventCallback(() => moveRowDown(idx));

  const isFieldMappingDisabled = withMappingData && !field.inclusionItem?.included;
  return (
    <div
      key={field.idx}
      className={clsx('flex-table__row flex-table__row--no-border', {
        'flex-table__row--odd': idx % 2 !== 0,
        highlight: highlightIds.current.has(field.uniqId)
      })}
    >
      <div
        className={clsx(
          'flex-table__cell flex-table__cell--w-fixed',
          styles.flexTableCellReorder,
          styles.flexTableCell,
          styles.reorderRow
        )}
        data-type='reorder'
      >
        <span
          role='button'
          className={clsx(
            styles.reorderRowIcon,
            styles.moveRowUp,
            'hevo-icon hevo-move-row',
            idx === 0 && styles.disabled
          )}
          onClick={moveUp}
        />
        <span
          role='button'
          className={clsx(
            styles.reorderRowIcon,
            styles.moveRowDown,
            'hevo-icon hevo-move-row',
            idx === fieldsLength - 1 && styles.disabled
          )}
          onClick={moveDown}
        />
      </div>

      {withMappingData && (
        <>
          <div
            className={clsx(
              'flex-table__cell flex-table__cell--w-fixed',
              styles.flexTableCell,
              styles.flexTableCellMapField
            )}
            data-type='map_field'
          >
            <div className={clsx(styles.fieldAction)}>
              <HdCheckbox
                name={field.uniqId}
                value={field.uniqId}
                dataId={dataIdGenerator(`check-field-${idx}`)}
                checked={field.inclusionItem.included}
                onChange={() => {
                  field.inclusionItem.toggleCheckedState();
                  forceUpdate();
                }}
              />
            </div>
          </div>
          <div
            className={clsx(
              'flex-table__cell flex-table__cell--w-fixed',
              styles.flexTableCell,
              styles.flexTableCellSourceField
            )}
            data-type='source_field'
          >
            <SourceSchema field={field} dataId={dataIdGenerator(idx)} />
          </div>
        </>
      )}

      {/* with MappingData */}
      <div
        className={clsx(
          'flex-table__cell flex-table__cell--w-fixed',
          styles.flexTableCell,
          styles.flexTableCellFieldName
        )}
        data-type='field_name'
      >
        <FastField
          name={`fields[${field.idx}].destination.name`}
          placeholder='Enter Field Name'
          shouldUpdate={shouldUpdateFormikTextFastField}
          hideHelperMessageIfEmpty
          classStyles={fieldClassName}
          debounceUpdates
          disabled={isFieldMappingDisabled}
          component={HdFormikTextField}
        />
      </div>

      <div
        className={clsx(
          'flex-table__cell flex-table__cell--w-fixed',
          styles.flexTableCell,
          styles.flexTableCellDataType
        )}
        data-type='data_type'
      >
        <div className='w-100'>
          <FastField
            shouldUpdate={shouldUpdateFormikDropdownFastField}
            component={HdFormikDropDown}
            options={withMappingData ? field.allowed_dest_types : dataTypes}
            placeholder='Select Data Type'
            creatableMode
            classStyles={fieldClassName}
            helperText={null}
            hideHelperMessageIfEmpty
            onChangeEventHandler={handleDataTypeChangeEvent}
            displayAccessor='type_display'
            disabled={isFieldMappingDisabled}
            valueAccessor='type'
            name={`fields[${field.idx}].dataType`}
            showLoading={dataTypesLoading}
          />
        </div>
      </div>

      {/** Primary Key */}
      {!isFireboltDest && (
        <div
          className={clsx(
            'flex-table__cell flex-table__cell--w-fixed',
            styles.flexTableCell,
            styles.flexTableCellKey
          )}
          data-type='key'
        >
          <div className={clsx(styles.fieldAction)}>
            <Field
              name={`fields[${field.idx}].isPrimaryKey`}
              disabled={isFieldMappingDisabled || isPrimaryKeyDisabled(field.dataType)}
              onChange={() => toggleKey(field.idx, 'isPrimaryKey')}
              component={HdFormikCheckBox}
            />

            {field.isPrimaryKey && field.dataType?.type_display === 'varchar' && isMySql && (
              <HdTooltip
                mode='surface'
                placement='right'
                disableInteractive={false}
                title='Please specify a character limit in the datatype'
              >
                <HdIcon className='ml-2' name='error-filled' />
              </HdTooltip>
            )}
          </div>
        </div>
      )}

      {/* PartitionKey */}
      {isBQDest && (
        <div
          className={clsx(
            'flex-table__cell flex-table__cell--w-fixed',
            styles.flexTableCell,
            styles.flexTableCellKey
          )}
          data-type='key'
        >
          <div
            className={clsx(styles.fieldAction, {
              'is-disabled': isPartitionKeyDisabled(field.dataType)
            })}
          >
            <HdRadioGroup currentValue={selectedPartitionKey} name='partition-key'>
              <HdRadio
                name='partition-key'
                dataId={dataIdGenerator(`partition-key-${idx}`)}
                value={field.uniqId}
                onChange={handlePartitionKeyChange}
                disabled={isPartitionKeyDisabled(field.dataType) || isFieldMappingDisabled}
              />
            </HdRadioGroup>
          </div>
        </div>
      )}

      {/* Cluster Key */}
      {(isBQDest || isSnowFlake) && (
        <div
          className={clsx(
            'flex-table__cell flex-table__cell--w-fixed',
            styles.flexTableCell,
            styles.flexTableCellKey
          )}
          data-type='key'
        >
          <div className={clsx(styles.fieldAction)}>
            <HdTooltip
              mode='surface'
              placement='right'
              disabled={isSnowFlake || !isBigQueryClusterKeyDisabled(field)}
              title={
                <BigQueryClusterKeyTooltipMessage
                  field={field}
                  bigQueryClusterKeysLimitReached={bigQueryClusterKeysLimitReached}
                />
              }
            >
              <div>
                <Field
                  name={`fields[${field.idx}].isClusterKey`}
                  onChange={() => toggleKey(field.idx, 'isClusterKey')}
                  disabled={isFieldMappingDisabled || isBigQueryClusterKeyDisabled(field)}
                  component={HdFormikCheckBox}
                />
              </div>
            </HdTooltip>
          </div>
        </div>
      )}

      {/* Sort Key | Primary Index (Firebolt) */}
      {(isFireboltDest || isRedshift) && (
        <div
          className={clsx(
            'flex-table__cell flex-table__cell--w-fixed',
            styles.flexTableCell,
            styles.flexTableCellKey,
            {
              [styles.flexTableCellShort]: isFireboltDest
            }
          )}
          data-type='key'
        >
          <div className={clsx(styles.fieldAction)}>
            <Field
              name={`fields[${field.idx}].isSortKey`}
              component={HdFormikCheckBox}
              onChange={() => toggleKey(field.idx, 'isSortKey')}
              disabled={isFieldMappingDisabled}
            />
          </div>
        </div>
      )}

      {/* Dist Key */}
      {isRedshift && (
        <div
          className={clsx(
            'flex-table__cell flex-table__cell--w-fixed',
            styles.flexTableCell,
            styles.flexTableCellKey
          )}
          data-key='dist'
          data-type='key'
        >
          <div className={clsx(styles.fieldAction)}>
            <HdRadioGroup name='dist' currentValue={selectedDistKey}>
              <HdRadio
                value={field.uniqId}
                dataId={dataIdGenerator(`dist-key-${idx}`)}
                disabled={isFieldMappingDisabled}
                onChange={handleDistKeyChange}
              />
            </HdRadioGroup>
          </div>
        </div>
      )}

      {/** Partition Key (Checkbox)  */}
      {(isFireboltDest || isDataBricks) && (
        <div
          className={clsx('flex-table__cell', styles.flexTableCellKey, {
            'flex-table__cell--w-fixed': isDataBricks,
            [styles.flexTableCellShort]: isFireboltDest
          })}
          data-type='key'
        >
          <div className={clsx(styles.fieldAction)}>
            <Field
              name={`fields[${field.idx}].isPartitionKey`}
              component={HdFormikCheckBox}
              onChange={() => toggleKey(field.idx, 'isPartitionKey')}
              disabled={
                isFieldMappingDisabled ||
                (isFireboltDest &&
                  fireboltTableType.tableType === FireboltTableTypeEnum.DIMENSION_TABLE)
              }
            />
          </div>
        </div>
      )}

      {/** Nullable Column */}
      {isFireboltDest && (
        <div
          className={clsx(
            'flex-table__cell',
            styles.flexTableCellKey,
            styles.flexTableCell,
            styles.flexTableCellShort
          )}
          data-type='key'
        >
          <div className={clsx(styles.fieldAction)}>
            <Field
              name={`fields[${field.idx}].optional`}
              onChange={() => toggleKey(field.idx, 'optional')}
              disabled={
                isFieldMappingDisabled ||
                field.isPrimaryKey ||
                isFireboltOptionalKeyDisabled(field.dataType?.type) ||
                field.isSortKey ||
                field.isPartitionKey
              }
              component={HdFormikCheckBox}
            />
          </div>
        </div>
      )}

      {!withMappingData && (
        <div className={clsx('flex-table__cell', styles.flexTableCell, styles.moreActions)}>
          <HdIconButton
            dataId={dataIdGenerator(`delete-field-${idx}`)}
            onClick={() => beforeRowDelete(field)}
          >
            <HdIcon size={3} name='delete' className='delete-row' />
          </HdIconButton>
        </div>
      )}
    </div>
  );
}

export const SchemaBuilderRowMemo = React.memo(SchemaBuilderRow, (prevProps, nextProps) => {
  const shouldReRender =
    prevProps.field === nextProps.field &&
    prevProps.idx === nextProps.idx &&
    prevProps.bigQueryClusterKeysLimitReached === nextProps.bigQueryClusterKeysLimitReached &&
    prevProps.selectedPartitionKey === nextProps.selectedPartitionKey &&
    prevProps.selectedDistKey === nextProps.selectedDistKey &&
    prevProps.fieldsLength === nextProps.fieldsLength &&
    prevProps.dataTypes === nextProps.dataTypes &&
    prevProps.field?.inclusionItem?.included === nextProps.field?.inclusionItem?.included &&
    prevProps.dataTypesLoading === nextProps.dataTypesLoading &&
    prevProps.fireboltTableType === nextProps.fireboltTableType;
  return shouldReRender;
});
